import { ApolloError } from "@apollo/client";
import EuroIcon from "@mui/icons-material/Euro";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { Control, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";

import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import Feedback from "~/modules/project/components/Feedback";
import UserBadge from "~/modules/user/components/UserBadge";
import FormTemplateFormDisplay from "~/shared/components/FormTemplate/FormTemplateFormDisplay";
import { Shared_FormTemplateFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";

import { ProjectReviewVisitV2TeamReportsRoute_WorkerReportFragment } from "../TeamReportsList/fragments.generated";

type Props = {
    report: ProjectReviewVisitV2TeamReportsRoute_WorkerReportFragment;
    form: Shared_FormTemplateFragment;
    onSubmit: (values: WorkerReportFormValues) => void;
    error?: ApolloError;
};

type ReportContentProps = Pick<Props, "form" | "report"> & {
    control: Control<WorkerReportFormValues>;
    feedbackRating: boolean | null | undefined;
};

function mapReportToForm(report: ProjectReviewVisitV2TeamReportsRoute_WorkerReportFragment): WorkerReportFormValues {
    return {
        feedbackRating: report.feedbackRating ?? null,
        feedbackComment: report.feedbackComment ?? null,
    };
}

function NotSubmittedState() {
    const { t } = useTranslation("project");
    return (
        <Alert severity="info">
            <Typography>{t("projectReviewVisitV2.teamReportsRoute.workerReport.notSubmitted")}</Typography>
        </Alert>
    );
}

function ReportContent({ form, report, control, feedbackRating }: ReportContentProps) {
    const { t } = useTranslation("project");
    return (
        <>
            <FormTemplateFormDisplay form={form} values={report.submittedForm ?? {}} />
            {report.notes && (
                <>
                    <Typography variant="caption">
                        {t("projectReviewVisitV2.teamReportsRoute.workerReport.labelNotes")}
                    </Typography>
                    <Typography>{report.notes}</Typography>
                </>
            )}
            <Feedback<WorkerReportFormValues>
                labelTitle={t("projectReviewVisitV2.teamReportsRoute.workerReport.labelRateReport")}
                labelComment={t("projectReviewVisitV2.teamReportsRoute.workerReport.labelFeedbackComment")}
                control={control}
                feedbackRating={feedbackRating}
            />
            <GaiaHookFormSubmitButton control={control}>
                {t("projectReviewVisitV2.teamReportsRoute.workerReport.btnSave")}
            </GaiaHookFormSubmitButton>
        </>
    );
}

export default function WorkerReport({ report, form, onSubmit, error }: Props) {
    const { t } = useTranslation("project");

    const { fieldworker } = report.plannerShift;

    const methods = useForm<WorkerReportFormValues>({
        values: mapReportToForm(report),
        mode: "onChange",
    });

    const { control, handleSubmit, watch, setError } = methods;

    useApplyErrorToForm(error, setError);

    const feedbackRating = watch("feedbackRating");

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack rowGap={2}>
                {fieldworker && (
                    <Stack direction="row" justifyContent="space-between">
                        <UserBadge
                            size={50}
                            profilePicture={fieldworker?.user.profilePictureMedia?.url}
                            names={[fieldworker.user.firstName, fieldworker.user.lastName]}
                            nameTextVariant="body1"
                            showName
                            linkTo={generatePath("/admin/users/:userId", {
                                userId: fieldworker.user.id,
                            })}
                            sx={{ gap: 2 }}
                        />
                        {report.plannerShift.readyForPayment === true && (
                            <Stack direction="row" gap={0.5} alignItems="center">
                                <Box
                                    component={EuroIcon}
                                    sx={theme => ({
                                        padding: 0.2,
                                        borderRadius: "100%",
                                        backgroundColor: theme.palette.primary.main,
                                        color: "white",
                                    })}
                                />
                                <Typography variant="body2" color="primary">
                                    {t("projectReviewVisitV2.teamReportsRoute.workerReport.labelShiftWillBePaid")}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                )}
                {report.submittedForm === null ? (
                    <NotSubmittedState />
                ) : (
                    <ReportContent form={form} report={report} control={control} feedbackRating={feedbackRating} />
                )}
            </Stack>
        </form>
    );
}

export type WorkerReportFormValues = {
    feedbackRating?: boolean | null;
    feedbackComment?: string | null;
};
